import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import DOMPurify from "dompurify";

const ICONS = {
  status: "spaciaz-icon-layer-2",
  type: "spaciaz-icon-buliding-2",
  area: "spaciaz-icon-format-square",
  commencement_date: "spaciaz-icon-calendar-2",
  price_range: "spaciaz-icon-dollar-circle",
  // features icons
  security: "spaciaz-icon-security",
  surveillance: "spaciaz-icon-system",
  fitness: "spaciaz-icon-weight",
  playarea: "spaciaz-icon-guard",
  // ...add more as needed
};

const ProjectDetailsMain = () => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get project id from URL
  const slug = window.location.pathname.split("/").pop();
  const projectId = slug;

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/infrastructure/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((data) => {
        setProject(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [projectId]);

  if (loading) return <div className="py-12 text-center">Loading...</div>;
  if (error) return <div className="py-12 text-center text-red-600">Error: {error}</div>;
  if (!project) return <div className="py-12 text-center">Project not found.</div>;

  // Example fallback for missing fields
  const {
    title,
    description,
    image,
    created_at,
    location,
    status,
    type,
    area,
    commencement_date,
    price_range,
    key_details = [],
    features = [],
    tags,
  } = project;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {tags && <meta name="keywords" content={tags.join(", ")} />}
      </Helmet>
      <div className="container flex flex-col gap-8 px-4 py-8 mx-auto mb-40 lg:flex-row">
        <div className="w-full">
          {/* Project Header: Location & Title */}
          <div className="mb-6">
            {location && (
              <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
                <i className="spaciaz-icon-marker" />
                <span>{location}</span>
              </div>
            )}
            <h1 className="mb-2 text-3xl font-bold text-center underline" style={{ color: "#A14D1F" }}>
              {title}
            </h1>
          </div>
          {/* Project Image */}
          {image && (
            <img src={image} alt={title} className="w-full mb-8 rounded-lg shadow" />
          )}
          {/* Project Description */}
          <div className="mb-8">
            <div
              className="leading-relaxed"
              style={{ color: "#4A2F24" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
            />
          </div>
          {/* Key Details */}
          {key_details.length > 0 && (
            <div className="mb-8">
              <h6 className="mb-2 text-lg font-semibold" style={{ color: "#A14D1F" }}>Key Details</h6>
              <ul className="pl-6 space-y-1 text-gray-700 list-disc">
                {key_details.map((item, idx) => (
                  <li key={idx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item) }} />
                ))}
              </ul>
            </div>
          )}
          {/* Features & Amenities */}
          {features.length > 0 && (
            <div>
              <h3 className="mb-4 text-xl font-semibold" style={{ color: "#A14D1F" }}>Features & Amenities</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex flex-col items-center p-4 bg-white border rounded-lg shadow" style={{ borderColor: "#A14D1F" }}>
                    <span className="mb-2 text-2xl">
                      <i className={ICONS[feature.icon] || "spaciaz-icon-security"} />
                    </span>
                    <span className="font-semibold">{feature.title}</span>
                    <span className="text-sm text-center text-gray-600">{feature.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectDetailsMain;